import { apiRoot } from '../../data/constants.ts';
import { getAppSettings } from '../../data/appSettings.ts';
import { openViaLightDomAnchor } from '../../util/utils.ts';
import { asApp } from '../../util/singletons.ts';
import { sendEv } from '../../data/events.ts';

/**
 * Embed HTML comes from the API and its links point out of the app, so they
 * always open in a new window. Set that on the anchors directly. The click
 * handler below covers hosts where a shadow-DOM anchor is not enough.
 */
function retargetLinks(container: HTMLElement): void {
  container.querySelectorAll('a[href]').forEach(a => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });
}

export class AsEmbedContent extends HTMLElement {
  static readonly tag = 'as-embed-content' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(/*css*/`
      :host { display: block; width: 100%; }
      .embed-content { 
        padding: 0; display: flow-root; overflow: hidden; 
        &  img, .embed-content video { max-width: 100%; height: auto; display: block; }
        & table { max-width: 100%; }
        & .embed-rich-text { 
          margin-top:0; 
          padding:1rem;

        }
        & p {
          padding: 1rem; 
          background:white; 
          border-radius:1rem; 
          box-shadow:var(--shadow-high);
          margin:0;
        }
        & h1 {
          font-style:italic; 
          margin: 0;
          padding: 0.25rem .5rem 1rem;
        }
      }
    `);
    return sheet;
  })();

  private _lastRequestId = 0;
  private _itemCount = 0;
  private _initPending = false;

  static get observedAttributes(): string[] {
    return ['embed-module-key', 'index'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsEmbedContent._sheet];
    this.shadowRoot!.addEventListener('click', this._handleLinkClick);
  }

  /**
   * Links in the embed live inside this shadow root. In GrabCAD's webview a
   * click there is retargeted to the shadow host and never opens a window, so
   * under that env we swallow the click and re-issue it through a light-DOM
   * anchor. Elsewhere the anchor's own target="_blank" does the job.
   */
  private _handleLinkClick = (ev: Event): void => {
    const path = ev.composedPath();
    const anchor = path.find((n): n is HTMLAnchorElement => n instanceof HTMLAnchorElement && n.hasAttribute('href'));
    if (!anchor) return;
    if (ev instanceof MouseEvent && (ev.button !== 0 || ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey)) return;
    const env = asApp()?.getAttribute('env') ?? null;
    if (!getAppSettings(env).externalLinksViaLightDomAnchor) return;
    ev.preventDefault();
    openViaLightDomAnchor(anchor.href);
  };

  connectedCallback(): void {
    this._scheduleInit();
  }

  attributeChangedCallback(name: string, oldVal: string, newVal: string): void {
    if (name === 'embed-module-key' && oldVal !== newVal) {
      this.isConnected ? this._scheduleInit() : void 0;
    } else if (name === 'index' && oldVal !== newVal) {
      this._updateVisibleIndex();
    }
  }

  private _scheduleInit(): void {
    if (this._initPending) return;
    this._initPending = true;
    Promise.resolve().then(() => {
      this._initPending = false;
      if (this.isConnected) this._init();
    });
  }

  private _updateVisibleIndex(): void {
    const index = parseInt(this.getAttribute('index') || '0', 10);
    this.shadowRoot!.querySelectorAll('.embed-content').forEach((el, i) => {
      (el as HTMLElement).style.display = i === index ? 'block' : 'none';
    });
  }

  private _clearContent(): void {
    this.shadowRoot!.querySelectorAll('.embed-content').forEach(el => el.remove());
    this._itemCount = 0;
  }

  /** Fetches the embed HTML for this key and injects it into the shadow root. */
  private async _init(): Promise<void> {
    const key = this.getAttribute('embed-module-key');
    if (!key) return;

    const requestId = ++this._lastRequestId;
    this._clearContent();

    try {
      const url = new URL(apiRoot, window.location.origin);
      url.searchParams.set('function', 'getEmbeds');
      url.searchParams.set('location', key);

      const response = await fetch(url.toString());
      if (requestId !== this._lastRequestId) return;
      if (!response.ok) return;

      const data = await response.json();
      if (!data?.success) return;

      const rawData = data.data;
      const items = Array.isArray(rawData) ? rawData : [rawData];

      items.forEach(item => {
        const html = typeof item === 'string' ? item : (item?.html ?? '');
        if (!html) return;

        const container = document.createElement('div');
        container.className = 'embed-content';
        container.innerHTML = html;
        retargetLinks(container);

        // If index is specified, hide all but the requested one.
        // Otherwise, show all (default for banner/simple usage).
        // Count against _itemCount, not the loop index: items with no html are
        // skipped, so the two drift apart and the wrong slide would show.
        // _updateVisibleIndex() counts rendered children, so this matches it.
        if (this.hasAttribute('index')) {
          const targetIndex = parseInt(this.getAttribute('index') || '0', 10);
          container.style.display = this._itemCount === targetIndex ? 'block' : 'none';
        }

        this.shadowRoot!.appendChild(container);
        this._itemCount++;
      });

      if (this._itemCount > 0) {
        sendEv(this, 'as-embed-ready', {
          detail: { count: this._itemCount, location: key }
        });
        this._dispatchLoaded();
      }
    } catch (err) {
      console.error('Failed to fetch API embed:', err);
    }
  }

  /** Content is in the shadow root. Fires `as-embed-load`; the unprefixed
   *  `embed-load` is deprecated. */
  private _dispatchLoaded(): void {
    sendEv(this, 'as-embed-load', { bubbles: false });
    //TODO:@REMOVE — deprecated unprefixed name.
    sendEv(this, 'embed-load', { bubbles: false });
  }
}

if (!customElements.get(AsEmbedContent.tag)) {
  customElements.define(AsEmbedContent.tag, AsEmbedContent);
} else {
  console.warn('as-embed-content already defined');
}
